import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PackageCheck, Scan, CheckCircle2, MapPin, Boxes, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SmallBoxPutAway = () => {
  const navigate = useNavigate();
  const staffRef = useRef(null);
  const boxRef = useRef(null);
  const binRef = useRef(null);

  // Staff gate (FG Put-Away staff list)
  const [staff, setStaff] = useState(null); // {staff_id, staff_name}
  const [staffInput, setStaffInput] = useState('');
  const [validatingStaff, setValidatingStaff] = useState(false);

  // Box scan
  const [boxInput, setBoxInput] = useState('');
  const [box, setBox] = useState(null);
  const [loadingBox, setLoadingBox] = useState(false);

  // Bin + submit
  const [bin, setBin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!staff) { setTimeout(() => staffRef.current?.focus(), 100); return; }
    if (!box) setTimeout(() => boxRef.current?.focus(), 100);
    else setTimeout(() => binRef.current?.focus(), 100);
  }, [staff, box]);

  const submitStaff = async (e) => {
    e?.preventDefault();
    const id = staffInput.trim();
    if (!id || validatingStaff) return;
    setValidatingStaff(true);
    try {
      const r = await apiClient.post('/wms/fg/put-away/clearance/validate-staff', { staff_id: id });
      setStaff({ staff_id: r.data.staff_id, staff_name: r.data.staff_name });
      setStaffInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Staff not found or inactive');
    } finally {
      setValidatingStaff(false);
    }
  };

  const scanBox = async (e) => {
    e?.preventDefault();
    const code = boxInput.trim();
    if (!code || loadingBox) return;
    setLoadingBox(true);
    setResult(null);
    try {
      const r = await apiClient.get(`/wms/fg/packs/small-box/scan`, { params: { box_code: code } });
      setBox(r.data);
      setBin(r.data.suggested_bin || '');
      if (!r.data.single_sku) {
        toast.error('This box has more than one product — split it first');
      } else {
        setTimeout(() => binRef.current?.focus(), 80);
      }
    } catch (err) {
      setBox(null);
      toast.error(err.response?.data?.detail || 'Box not eligible');
    } finally {
      setLoadingBox(false);
    }
  };

  const resetBox = () => {
    setBox(null);
    setBin('');
    setBoxInput('');
    setTimeout(() => boxRef.current?.focus(), 80);
  };

  const submitPutAway = async (e) => {
    e?.preventDefault();
    if (!box) { toast.error('Scan a box first'); return; }
    if (!box.single_sku) { toast.error('Split the mixed box before putting it away'); return; }
    const b = bin.trim();
    if (!b) { toast.error('Scan the destination bin'); binRef.current?.focus(); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/small-box/put-away', {
        box_code: box.box_code,
        bin_code: b,
        staff_id: staff?.staff_id,
        staff_name: staff?.staff_name,
      });
      setResult(r.data);
      toast.success(r.data.message || 'Box stored in FG');
      resetBox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Put-away failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- STAFF GATE ----------
  if (!staff) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="smallbox-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <PackageCheck className="w-6 h-6" />
            <h1 className="text-lg font-bold">Small Box Put Away</h1>
          </div>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <Card className="w-full max-w-sm">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-center mb-4">Scan your Staff ID</h2>
              <form onSubmit={submitStaff} className="space-y-3">
                <Input ref={staffRef} value={staffInput} onChange={(e) => setStaffInput(e.target.value)}
                  placeholder="Staff ID" className="text-center text-lg h-12" data-testid="smallbox-staff-input" autoFocus />
                <Button type="submit" className="w-full h-11 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={validatingStaff} data-testid="smallbox-staff-submit">
                  {validatingStaff ? 'Checking…' : 'Start'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---------- MAIN ----------
  return (
    <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="smallbox-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <PackageCheck className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-bold leading-tight">Small Box Put Away</h1>
              <p className="text-[11px] text-[#E8DCC8]">{staff.staff_name} ({staff.staff_id})</p>
            </div>
          </div>
        </div>
        <Button size="sm" variant="ghost" className="text-[#F9FAFB] hover:bg-white/10 text-xs" onClick={() => { setStaff(null); resetBox(); }} data-testid="smallbox-switch-staff">Switch</Button>
      </header>

      <main className="flex-1 px-4 py-4 max-w-md mx-auto w-full space-y-4">
        <p className="text-xs text-[#5A5F6E] bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-3 py-2">
          Scan a <span className="font-semibold">Common-Pool small box</span> → scan the FG bin. The whole box is stored as one pack.
        </p>

        {/* Scan box */}
        <form onSubmit={scanBox} className="flex gap-2">
          <Input ref={boxRef} value={boxInput} onChange={(e) => setBoxInput(e.target.value)}
            placeholder={loadingBox ? 'Loading…' : 'Scan small-box code'} className="h-12 text-lg" data-testid="smallbox-box-input" disabled={loadingBox || !!box} />
          <Button type="submit" className="h-12 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={loadingBox || !!box} data-testid="smallbox-box-scan-btn">
            <Scan className="w-5 h-5" />
          </Button>
        </form>

        {box && (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-3 space-y-3" data-testid="smallbox-box-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Boxes className="w-5 h-5 text-fuchsia-600" />
                <div>
                  <p className="font-semibold">{box.box_number}</p>
                  <p className="text-xs text-[#5A5F6E]">Box code: {box.box_code}</p>
                </div>
              </div>
              <button onClick={resetBox} className="text-xs text-red-500 underline" data-testid="smallbox-change-box">Change</button>
            </div>

            <div className="flex items-center justify-between bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-4 py-3">
              <span className="text-sm text-fuchsia-800 font-medium">Pieces in box</span>
              <span className="text-2xl font-bold text-fuchsia-700" data-testid="smallbox-total-qty">{box.total_qty}</span>
            </div>

            {(box.skus || []).map((s, i) => (
              <div key={i} className="border border-[#E5DFD0] rounded px-3 py-2" data-testid={`smallbox-sku-line-${i}`}>
                <p className="font-semibold text-[#1A1F2E] text-sm">{s.design_name || s.sku_code}</p>
                <p className="text-xs text-[#5A5F6E]">{s.sku_code} · {s.shade} · {s.size} · <span className="font-semibold">{s.qty} pcs</span></p>
              </div>
            ))}

            {!box.single_sku ? (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="smallbox-multi-warning">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-xs text-red-700">{box.warning || 'This box has more than one product. Split it into single-product boxes before putting it away.'}</p>
              </div>
            ) : (
              <form onSubmit={submitPutAway} className="space-y-2 pt-1">
                {box.suggested_bin && (
                  <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="smallbox-bin-suggestion">
                    Suggested bin: <span className="font-bold">{box.suggested_bin}</span>
                    {box.bin_info?.reason ? ` — ${box.bin_info.reason}` : ''}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#5A5F6E]" />
                  <Input ref={binRef} value={bin} onChange={(e) => setBin(e.target.value)}
                    placeholder="Scan destination bin" className="h-11" data-testid="smallbox-bin-input" />
                </div>
                <Button type="submit" className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="smallbox-putaway-btn">
                  <CheckCircle2 className="w-5 h-5 mr-1" />
                  {submitting ? 'Storing…' : `Put away ${box.total_qty} piece(s)`}
                </Button>
              </form>
            )}
          </div>
        )}

        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="smallbox-result">
            <p className="text-emerald-800 font-medium">{result.message}</p>
            <p className="text-xs text-emerald-700 mt-1">Pack {result.pack_code} · {result.placed_count} pcs · bin {result.bin_code}</p>
            <p className="text-xs mt-1 text-emerald-700" data-testid="smallbox-result-usns">
              {result.real_usns
                ? `Stored with the ${result.real_usn_count} real USNs from the box`
                : `${result.placed_count - (result.real_usn_count || 0)} pc(s) stored with placeholder codes (box USNs not traceable)`}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SmallBoxPutAway;
